"use client";

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { PrismicNextImage } from "@prismicio/next";
import { animate, AnimationPlaybackControlsWithThen, useInView } from "framer-motion";

/**
 * Props for `StatsSection`.
 */
export type StatsSectionProps = SliceComponentProps<Content.StatsSectionSlice>;

/**
 * Component for "StatsSection" Slices.
 */
const StatsSection: FC<StatsSectionProps> = ({ slice }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number>();
  const valueRefs = useRef<(HTMLHeadingElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(containerRef, {
    once: true
  });

  useEffect(() => {
    const controls: AnimationPlaybackControlsWithThen[] = [];
    if (isInView) {
      valueRefs.current.forEach((node, idx) => {
        if (!node) return;
  
        if (node) {
          const currentCount = slice.primary.stats[idx].count ?? 0;
          const suffix = slice.primary.stats[idx].suffix;
          node.textContent = `0${suffix}`;
  
          controls.push(
            animate(0, currentCount, {
              duration: 1,
              ease: "easeOut",
              onUpdate: (latestValue) => {
                node.textContent = `${latestValue.toFixed(0)}${suffix}`;
              },
            })
          );
        }
      });
    }
    return () => controls.forEach((control) => control.stop());
  }, [isInView]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <Bounded as={"div"} className="py-10 bg-white">
        <div className="container mx-auto grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-6">
          {slice.primary.stats.map((item, idx) => (
            // Render the item
            <div
              key={idx}
              className="flex items-center gap-6 bg-white py-5 px-9 rounded-[20px] border border-[#EBF7FF]"
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(undefined)}
            >
              <div className="h-[60px] w-[60px] flex items-center justify-center rounded-[12px] bg-[#EBF7FF]">
                <PrismicNextImage
                  className="h-8 w-8"
                  field={hoveredIndex === idx ? item.reverted_icon : item.icon}
                />
              </div>
              <div ref={containerRef}>
                <h3
                  ref={(element) => {
                    valueRefs.current[idx] = element;
                  }}
                  className="font-archivo text-3xl font-semibold leading-10 text-[#067AC2]"
                >
                  {`${item.count}${item.suffix}`}
                </h3>
                <p className="font-archivo text-base font-medium text-[#033D61]">
                  {item.sub_title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Bounded>
    </section>
  );
};

export default StatsSection;
